// ── trainingTokens.js ─────────────────────────────────────────────────────────
// All CSS variables, keyframes, and shared utility classes for TrainingPage.
// FIXES:
//  - Added will-change to animated elements to trigger GPU compositing
//  - Reduced concurrent animations (orbs/particles paused on low-end via media query)
//  - Full responsive breakpoints for 480px, 640px, 768px, 900px, 1024px
//  - Removed backdrop-filter on non-critical elements (layout-paint cost)
//  - Added contain: layout style paint on heavy sections
//  - Fixed tp-hero-actions stacking on mobile
//  - Fixed tp-section padding collapsing on small screens

export const TRAINING_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@500;600;700&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;600;700&display=swap');

  /* ── Design tokens ──────────────────────────────────────────────────────── */
  :root {
    --tp-bg:         #020408;
    --tp-bg2:        #030610;
    --tp-surface:    #080c18;
    --tp-surface2:   #0c1020;
    --tp-surface3:   #101528;
    --tp-line:       rgba(255,255,255,.055);
    --tp-line2:      rgba(255,255,255,.11);

    --tp-t1:         #e8ecff;
    --tp-t2:         #7b86b0;
    --tp-t3:         #363e60;

    --tp-blue:       #4d7fff;
    --tp-indigo:     #5b5fed;
    --tp-violet:     #8248f5;
    --tp-pink:       #e040a0;
    --tp-cyan:       #18d4f0;
    --tp-amber:      #f59e0b;
    --tp-gold:       #fbbf24;
    --tp-green:      #0fd68a;
    --tp-red:        #f04444;
    --tp-discord:    #5865f2;
    --tp-discord2:   #7289da;
    --tp-jazz:       #e52020;
    --tp-easy:       #00a651;
    --tp-bank:       #1a56db;

    --tp-enroll-glow: rgba(77,127,255,.55);
    --tp-enroll-glow2: rgba(130,72,245,.45);

    --tp-display:    'Clash Display', sans-serif;
    --tp-body:       'Instrument Sans', sans-serif;
    --tp-mono:       'JetBrains Mono', monospace;
    --tp-radius:     18px;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .tp-root {
    background: var(--tp-bg);
    color: var(--tp-t1);
    font-family: var(--tp-body);
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* ── Shared layout ─────────────────────────────────────────────────────── */
  .tp-section       { padding: 7rem 1.5rem; }
  .tp-section-alt   {
    background: var(--tp-surface);
    border-top: 1px solid var(--tp-line);
    border-bottom: 1px solid var(--tp-line);
  }
  .tp-container     { max-width: 1200px; margin: 0 auto; }
  .tp-container-sm  { max-width: 860px; margin: 0 auto; }

  /* ── Eyebrow ───────────────────────────────────────────────────────────── */
  .tp-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: .68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .18em;
    color: var(--tp-blue);
    margin-bottom: 1rem;
  }
  .tp-eyebrow-line {
    width: 20px; height: 2px;
    background: linear-gradient(90deg, var(--tp-blue), transparent);
    border-radius: 2px;
  }

  /* ── Section headings ──────────────────────────────────────────────────── */
  .tp-section-h2 {
    font-family: var(--tp-display);
    font-size: clamp(2.4rem, 5.5vw, 4rem);
    font-weight: 700;
    letter-spacing: -.03em;
    color: var(--tp-t1);
    margin: 0 0 1rem;
    line-height: 1.05;
  }
  .tp-section-h2 em {
    font-style: normal;
    background: linear-gradient(135deg, var(--tp-blue) 0%, var(--tp-violet) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .tp-section-sub {
    font-size: 1.05rem;
    color: var(--tp-t2);
    line-height: 1.85;
    max-width: 580px;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     ENROLLMENT PRIMARY BUTTON
     ══════════════════════════════════════════════════════════════════════════ */
  .tp-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 1rem 2.5rem;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--tp-blue) 0%, var(--tp-violet) 55%, var(--tp-pink) 100%);
    background-size: 200% 200%;
    border: none;
    color: #fff;
    font-family: var(--tp-body);
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow:
      0 0 0 1px rgba(255,255,255,.12) inset,
      0 8px 32px rgba(77,127,255,.45),
      0 2px 6px rgba(130,72,245,.35),
      0 0 60px rgba(77,127,255,.18);
    transition:
      transform .28s cubic-bezier(.16,1,.3,1),
      box-shadow .28s cubic-bezier(.16,1,.3,1),
      background-position .6s ease;
    text-decoration: none;
    letter-spacing: -.01em;
    position: relative;
    overflow: hidden;
    animation: tp-enroll-breathe 3s ease-in-out infinite;
    will-change: transform, box-shadow;
  }
  .tp-btn-primary::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.22), transparent);
    transform: skewX(-20deg);
    animation: tp-btn-sweep 2.8s ease-in-out infinite;
    will-change: left;
  }
  .tp-btn-primary::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,.18) 0%, transparent 50%);
    opacity: 0;
    transition: opacity .28s;
    border-radius: inherit;
  }
  .tp-btn-primary:hover {
    transform: translateY(-4px) scale(1.03);
    background-position: right center;
    box-shadow:
      0 0 0 1px rgba(255,255,255,.18) inset,
      0 16px 52px rgba(77,127,255,.62),
      0 4px 12px rgba(130,72,245,.5),
      0 0 100px rgba(77,127,255,.28);
  }
  .tp-btn-primary:hover::after { opacity: 1; }
  .tp-btn-primary:active { transform: translateY(-1px) scale(1.01); }

  .tp-btn-enroll-hero {
    padding: 1.1rem 2.75rem;
    font-size: 1.05rem;
    border-radius: 16px;
  }

  @keyframes tp-enroll-breathe {
    0%, 100% {
      box-shadow:
        0 0 0 1px rgba(255,255,255,.12) inset,
        0 8px 32px rgba(77,127,255,.45),
        0 2px 6px rgba(130,72,245,.35),
        0 0 60px rgba(77,127,255,.18);
    }
    50% {
      box-shadow:
        0 0 0 1px rgba(255,255,255,.18) inset,
        0 8px 40px rgba(77,127,255,.65),
        0 2px 10px rgba(130,72,245,.52),
        0 0 90px rgba(77,127,255,.32);
    }
  }
  @keyframes tp-btn-sweep {
    0%   { left: -100%; opacity: 0; }
    20%  { opacity: 1; }
    80%  { opacity: .6; }
    100% { left: 160%; opacity: 0; }
  }

  /* ── Secondary button ──────────────────────────────────────────────────── */
  .tp-btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: .9rem 2.25rem;
    border-radius: 14px;
    background: rgba(255,255,255,.028);
    border: 1px solid var(--tp-line2);
    color: var(--tp-t2);
    font-family: var(--tp-body);
    font-size: .95rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color .2s, color .2s, transform .2s, background .2s;
    text-decoration: none;
  }
  .tp-btn-secondary:hover {
    border-color: rgba(77,127,255,.32);
    color: var(--tp-t1);
    background: rgba(77,127,255,.045);
    transform: translateY(-2px);
  }

  /* ── Discord button ────────────────────────────────────────────────────── */
  .tp-btn-discord {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: .8rem 1.75rem;
    border-radius: 14px;
    background: rgba(88,101,242,.065);
    border: 1px solid rgba(88,101,242,.22);
    color: rgba(114,137,218,.85);
    font-family: var(--tp-body);
    font-size: .88rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: none;
    transition: all .22s cubic-bezier(.16,1,.3,1);
    text-decoration: none;
    letter-spacing: -.005em;
    opacity: .82;
    will-change: transform;
  }
  .tp-btn-discord:hover {
    background: rgba(88,101,242,.11);
    border-color: rgba(88,101,242,.34);
    color: var(--tp-discord2);
    opacity: 1;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(88,101,242,.14);
  }

  /* ── Animations ────────────────────────────────────────────────────────── */
  @keyframes tp-fadein {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-fadein-up {
    from { opacity: 0; transform: translateY(36px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-fadein-scale {
    from { opacity: 0; transform: scale(.92) translateY(16px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes tp-float {
    0%, 100% { transform: translateY(0) scale(1); }
    50%       { transform: translateY(-22px) scale(1.015); }
  }
  @keyframes tp-spin { to { transform: rotate(360deg); } }
  @keyframes tp-pop {
    from { transform: scale(.55); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }
  @keyframes tp-grid-drift {
    0%   { background-position: 0 0; }
    100% { background-position: 56px 56px; }
  }
  @keyframes tp-shimmer {
    0%, 100% { transform: translateX(-120%); }
    50%       { transform: translateX(120%); }
  }
  @keyframes tp-shimmer-bar {
    0%   { left: -80%; }
    100% { left: 160%; }
  }
  @keyframes tp-pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .45; transform: scale(.65); }
  }
  @keyframes tp-scroll-pulse {
    0%, 100% { opacity: .35; transform: scaleY(1); }
    50%       { opacity: 1; transform: scaleY(.65); }
  }
  @keyframes tp-particle-rise {
    0%   { opacity: 0; transform: translateY(0) scale(0); }
    10%  { opacity: .75; }
    90%  { opacity: .18; }
    100% { opacity: 0; transform: translateY(-100vh) scale(1.5); }
  }
  @keyframes tp-bar-fill {
    from { width: 0; }
    to   { width: var(--bar-w, 60%); }
  }
  @keyframes tp-slide-in-left {
    from { opacity: 0; transform: translateX(-32px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-slide-in-right {
    from { opacity: 0; transform: translateX(32px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-counter-in {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-glow-ring {
    0%, 100% { box-shadow: 0 0 0 0 rgba(77,127,255,0), 0 0 0 0 rgba(130,72,245,0); }
    50%       { box-shadow: 0 0 0 6px rgba(77,127,255,.12), 0 0 0 12px rgba(130,72,245,.06); }
  }
  @keyframes tp-border-rotate {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes tp-card-enter {
    from { opacity: 0; transform: translateY(28px) scale(.97); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes tp-line-grow {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  @keyframes tp-success-ring {
    0%   { transform: scale(1); opacity: .6; }
    100% { transform: scale(2.2); opacity: 0; }
  }

  /* ── Scroll-reveal ─────────────────────────────────────────────────────── */
  .tp-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
    will-change: opacity, transform;
  }
  .tp-reveal.tp-visible { opacity: 1; transform: none; }
  .tp-reveal-left {
    opacity: 0;
    transform: translateX(-32px);
    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
    will-change: opacity, transform;
  }
  .tp-reveal-left.tp-visible { opacity: 1; transform: none; }
  .tp-reveal-right {
    opacity: 0;
    transform: translateX(32px);
    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
    will-change: opacity, transform;
  }
  .tp-reveal-right.tp-visible { opacity: 1; transform: none; }

  .tp-stagger-1 { transition-delay: .08s; }
  .tp-stagger-2 { transition-delay: .16s; }
  .tp-stagger-3 { transition-delay: .24s; }
  .tp-stagger-4 { transition-delay: .32s; }
  .tp-stagger-5 { transition-delay: .40s; }
  .tp-stagger-6 { transition-delay: .48s; }

  /* ══════════════════════════════════════════════════════════════════════════
     RESPONSIVE BREAKPOINTS — comprehensive fixes
     ══════════════════════════════════════════════════════════════════════════ */

  /* ── 1024px ── */
  @media (max-width: 1024px) {
    .tp-discord-grid { gap: 3rem; }
    .tp-tracks { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
  }

  /* ── 900px ── */
  @media (max-width: 900px) {
    .tp-stats        { grid-template-columns: repeat(3, 1fr); }
    .tp-discord-grid { grid-template-columns: 1fr; gap: 3rem; }
    .tp-steps        { grid-template-columns: repeat(2, 1fr); gap: 2.5rem; }
    .tp-steps::before { display: none; }
    .tp-perks        { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
  }

  /* ── 768px ── */
  @media (max-width: 768px) {
    .tp-section { padding: 5rem 1.25rem; }
    .tp-section-h2 { font-size: clamp(2rem, 6vw, 3rem); }
    .tp-section-sub { font-size: .95rem; }
    .tp-hero-actions {
      flex-direction: column;
      align-items: center;
      gap: .875rem;
      width: 100%;
    }
    .tp-enroll-wrap { width: 100%; }
    .tp-btn-primary  { width: 100%; justify-content: center; }
    .tp-btn-discord  { width: 100%; justify-content: center; }
    .tp-hero-discord-label { text-align: center; }
  }

  /* ── 640px ── */
  @media (max-width: 640px) {
    .tp-section { padding: 4rem 1rem; }
    .tp-stats              { grid-template-columns: repeat(2, 1fr); }
    .tp-stat               { border-right: none; border-bottom: 1px solid var(--tp-line); }
    .tp-stat:nth-child(odd)  { border-right: 1px solid var(--tp-line); }
    .tp-methods            { grid-template-columns: 1fr; }
    .tp-form-row           { grid-template-columns: 1fr; }
    .tp-payment-body       { padding: 1.5rem; }
    .tp-payment-header     { padding: 1.75rem 1.5rem; }
    .tp-steps              { grid-template-columns: 1fr; }
    .tp-pricing-card       { padding: 2.5rem 1.5rem; }
    .tp-discord-stats-row  { flex-direction: column; gap: 1.5rem; }
    .tp-cta-actions-row    { flex-direction: column; align-items: center; width: 100%; }
    .tp-cta-actions-row .tp-btn-primary  { width: 100%; justify-content: center; }
    .tp-cta-actions-row .tp-btn-discord  { width: 100%; justify-content: center; }
    .tp-discord-actions-row { flex-direction: column; align-items: flex-start; width: 100%; }
    .tp-discord-actions-row .tp-btn-primary { width: 100%; justify-content: center; }
    .tp-discord-actions-row .tp-btn-discord { width: 100%; justify-content: center; }
    .tp-perks              { grid-template-columns: 1fr; }
    .tp-tracks             { grid-template-columns: 1fr 1fr; }
    .tp-pricing-amount     { font-size: clamp(3rem, 15vw, 5rem); }
  }

  /* ── 480px ── */
  @media (max-width: 480px) {
    .tp-section { padding: 3.5rem .875rem; }
    .tp-tracks  { grid-template-columns: 1fr; }
    .tp-h1      { font-size: clamp(2.6rem, 11vw, 4rem); }
    .tp-cta-perks { gap: .5rem; }
    .tp-payment-card { border-radius: 18px; }
    .tp-pricing-card { border-radius: 18px; }
    .tp-stat-n  { font-size: 1.7rem; }
    .tp-methods { grid-template-columns: 1fr 1fr; }
  }

  /* ── Reduce motion for low-power / accessibility ── */
  @media (prefers-reduced-motion: reduce) {
    .tp-orb-1, .tp-orb-2, .tp-orb-3 { animation: none; }
    .tp-particle { display: none; }
    .tp-hero-grid { animation: none; }
    .tp-btn-primary { animation: none; }
    .tp-btn-primary::before { animation: none; }
    .tp-stats::before { animation: none; }
    .tp-discord-card-banner::after { animation: none; }
    .tp-pricing-card::before { animation: none; }
    .tp-scroll-line { animation: none; }
    .tp-reveal, .tp-reveal-left, .tp-reveal-right {
      opacity: 1; transform: none; transition: none;
    }
  }
`;
